import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { DateRangeSelects, UniversalSelect } from '@feathr/components';
import { useTimeFrameSelect } from '@feathr/extender/hooks';
import { flattenError } from '@feathr/hooks';

interface IDateRangeOption {
  id: string;
  name: string;
  value: [string | undefined, string | undefined];
}

interface IProps {
  customDateRange: [string | undefined, string | undefined];
  onDateRangeChange: (option: IDateRangeOption) => void;
  onCustomDateRangeChange: (value: [string | undefined, string | undefined]) => void;
  selectedDateRange: string;
  validationError: string[] | undefined;
}

function DateRangeSelect({
  customDateRange,
  onDateRangeChange,
  onCustomDateRangeChange,
  selectedDateRange,
  validationError,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { options: timeFrameOptions } = useTimeFrameSelect();

  const dateRangeOptions: IDateRangeOption[] = [
    { id: 'all_time', name: t('All time'), value: [undefined, undefined] },
    ...timeFrameOptions.map((opt) => ({
      id: opt.id,
      name: opt.name,
      value: opt.value as [string | undefined, string | undefined],
    })),
    { id: 'custom', name: t('Custom'), value: [undefined, undefined] },
  ];

  return (
    <>
      <UniversalSelect<IDateRangeOption>
        label={t('Date range')}
        name={'date_range'}
        onSelectSingle={onDateRangeChange}
        options={dateRangeOptions}
        validationError={flattenError(validationError)}
        value={dateRangeOptions.find((opt) => opt.id === selectedDateRange)}
      />
      {selectedDateRange === 'custom' && (
        <DateRangeSelects onChange={onCustomDateRangeChange} t={t} value={customDateRange} />
      )}
    </>
  );
}

export default DateRangeSelect;
